import { CaretArrowDownIcon } from '@synoptic/ui-kit/icons/react/caret-arrow-down.tsx';
import { LinkButton } from '@synoptic/ui-kit/link-button/link-button.tsx';
import { UseComboboxPropGetters } from '@synoptic/ui-kit/multi-select-combobox/multi-select-combobox.js';
import {
  emptyDropdownPlaceholder,
  loadingContainer,
  multiSelectMenu,
  multiSelectMenuGroupTitle,
  multiSelectMenuItem,
  multiSelectMenuItemDisabled,
} from '@synoptic/ui-kit/multi-select-combobox/multi-select-group.css.ts';
import { Spinner } from '@synoptic/ui-kit/spinner/spinner.js';
import { ReactNode, useMemo, useState } from 'react';
import { moreResultsHint } from './paginated-multiselect-result.css.ts';

const INITIAL_VISIBLE_COUNT = 3;
const SHOW_MORE_INCREMENT = 3;
const MAX_ITEMS_HINT_THRESHOLD = 10;

export function PaginatedMultiSelectResult<T extends { id: string }>({
  groupName,
  showLabel,
  items,
  selectedItems,
  getItemProps,
  isItemDisabled,
  isInitialLoading,
  emptyPlaceholder,
  showMaxItemsHint = false,
  children,
}: {
  groupName?: string;
  showLabel?: boolean;
  items: T[];
  selectedItems: T[];
  getItemProps: UseComboboxPropGetters<T>['getItemProps'];
  isItemDisabled?: (item: T) => boolean;
  isInitialLoading: boolean;
  emptyPlaceholder?: string;
  showMaxItemsHint?: boolean;
  children: (item: T) => ReactNode;
}) {
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);
  const [lastItems, setLastItems] = useState(items);

  // Reset visible count when items change (e.g., new search query)
  if (items !== lastItems) {
    setLastItems(items);
    setVisibleCount(INITIAL_VISIBLE_COUNT);
  }

  const itemsToRender = useMemo(() => {
    return (
      items?.filter((item) => !selectedItems.find((v) => v.id === item.id)) ||
      []
    );
  }, [selectedItems, items]);

  const visibleItems = useMemo(() => {
    return itemsToRender.slice(0, visibleCount);
  }, [itemsToRender, visibleCount]);

  const hasMore = visibleCount < itemsToRender.length;
  const shouldShowHint =
    showMaxItemsHint &&
    !hasMore &&
    (items?.length ?? 0) >= MAX_ITEMS_HINT_THRESHOLD;

  const handleShowMore = () => {
    setVisibleCount((prev) => prev + SHOW_MORE_INCREMENT);
  };

  return (
    <div>
      {showLabel && (
        <span className={multiSelectMenuGroupTitle}>{groupName}</span>
      )}
      {isInitialLoading && (
        <div className={loadingContainer}>
          <Spinner />
        </div>
      )}
      {visibleItems.length > 0 && !isInitialLoading && (
        <ul className={multiSelectMenu}>
          {visibleItems.map((item) => (
            <li
              className={
                isItemDisabled?.(item)
                  ? multiSelectMenuItemDisabled
                  : multiSelectMenuItem
              }
              key={item.id}
              {...getItemProps({
                item: item,
                id: item.id,
              })}
            >
              {children(item)}
            </li>
          ))}
        </ul>
      )}
      {itemsToRender.length === 0 && !isInitialLoading && (
        <p className={emptyDropdownPlaceholder}>
          {emptyPlaceholder || 'No items found'}
        </p>
      )}
      {hasMore && !isInitialLoading && (
        <div className={loadingContainer}>
          <LinkButton
            startIcon={<CaretArrowDownIcon width={16} height={16} />}
            size="medium"
            onClick={handleShowMore}
          >
            Show more
          </LinkButton>
        </div>
      )}
      {shouldShowHint && (
        <p className={moreResultsHint}>Please type more to see more results…</p>
      )}
    </div>
  );
}
